import React, { useEffect, useRef, useState } from 'react';

import { ImperativePanelGroupHandle, PanelGroup } from '@bringg-frontend/resizable-panel';
import { DetailsColumns, ViewColumn } from '@bringg-frontend/monitoring-shared';
import { observer } from 'mobx-react-lite';
import { hasFeatureFlag } from '@bringg-frontend/global-stores';
import classNames from 'classnames';
import { GroupData, ResponseItem } from '@bringg/types';

import useOrderExplorerStore from '../../stores/use-order-explorer-store';
import DetailsNavBar from './details-nav-ber/details-nav-bar';
import { useStores } from 'bringg-web/recipes';
import { columnsProvider } from 'bringg-web/features/monitoring/routes-monitoring-screen/utils/details-columns-provider';
import { useColumns } from './hooks/use-columns';
import { isGroupRow } from '../../../shared/utils/grouping';

import styles from './orders-details.module.scss';

export const ORDERS_DETAILS_GROUP_ID = 'orders-details';
export const ORDER_DETAILS_PANEL_SIZES_KEY = 'orders-details-panel-group';

const customStyle = { group: styles.groupRow };

interface Props {
	onColumnsChange: (sizeRatioChange: number) => void;
}

const getColumnWithScroll = (columnsToView: ViewColumn<ResponseItem, GroupData>[]) => {
	for (let index = columnsToView.length - 1; index >= 0; index--) {
		const { id, size } = columnsToView[index];
		if (size > 0) {
			return id;
		}
	}
};

const OrdersDetails = ({ onColumnsChange }: Props) => {
	const {
		usersStore: { currentUser }
	} = useStores();
	const columnConfigEnabled = hasFeatureFlag(currentUser, 'routes_monitoring_columns_config_enabled');

	const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);
	const columnsLayoutMapRef = useRef<Map<string, number>>(new Map());
	const [columnWithScrollBar, setColumnWithScrollBar] = useState<string>();

	const {
		orderExplorerStore: { currentTableDefinition, isScrolled }
	} = useOrderExplorerStore();
	const { flatTableItems, total } = currentTableDefinition;

	const { columnsToView, columnsConfig, onSelectedColumnsChange, getDefaultConfig } = useColumns(
		ORDERS_DETAILS_GROUP_ID,
		columnsLayoutMapRef.current,
		onColumnsChange
	);

	useEffect(() => {
		return () => {
			columnsProvider.saveUserColumnSizes.cancel();
		};
	}, []);

	const onLayout = (sizes: number[]) => {
		const layoutMap = columnsLayoutMapRef.current;
		layoutMap.clear();

		columnsToView.forEach((item, index) => {
			const size = sizes[index];
			layoutMap.set(item.id, size);
		});

		let columnToScrollBar;

		for (let index = columnsToView.length - 1; index >= 0; index--) {
			const size = sizes[index];
			const columnId = columnsToView[index].id;

			if (!columnToScrollBar && size > 0) {
				columnToScrollBar = columnId;
				break;
			}
		}

		setColumnWithScrollBar(columnToScrollBar);

		columnConfigEnabled && columnsProvider.saveUserColumnSizes(ORDER_DETAILS_PANEL_SIZES_KEY, sizes);
	};

	useEffect(() => {
		const columnToScrollBar = getColumnWithScroll(columnsToView);
		setColumnWithScrollBar(columnToScrollBar);

		const layoutMap = columnsLayoutMapRef.current;
		if (layoutMap.size === 0) return;

		if (columnsToView.length === layoutMap.size) {
			const panelGroup = panelGroupRef.current;

			panelGroup.setLayout(columnsToView.map(column => column.size));
		}
	}, [columnsToView]);

	return (
		<div className={styles.ordersDetails} data-test-id="orders-details-table">
			<DetailsNavBar
				columnsConfigProps={{
					columnsConfig,
					onSelectedColumnsChange,
					getDefaultColumns: getDefaultConfig
				}}
			/>
			<PanelGroup
				ref={panelGroupRef}
				direction="horizontal"
				disablePointerEventsDuringResize={true}
				className={styles.panelGroup}
				onLayout={onLayout}
				id={ORDERS_DETAILS_GROUP_ID}
			>
				<DetailsColumns<ResponseItem, GroupData>
					groupId={ORDERS_DETAILS_GROUP_ID}
					columns={columnsToView}
					data={flatTableItems}
					rowCount={total}
					columnWithScrollBar={columnWithScrollBar}
					customStyle={customStyle}
					isGroupFn={isGroupRow}
				/>
			</PanelGroup>
			<div className={classNames(styles.shadow, { [styles.scroll]: isScrolled })} />
		</div>
	);
};

export default observer(OrdersDetails);
